import { type MouseEvent, useCallback, useState } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { Download, Link, Pencil, Printer, Share2 } from 'lucide-react';

import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useCopiedTooltip } from '@modules/_shared/hooks/useCopiedTooltip';
import { useDocumentUrl } from '@modules/_shared/hooks/useDocumentUrl';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  useReportDownloadHandler,
  useReportPrintHandler,
  useTrackedDocumentLink,
} from '@modules/_shared/utils/reportHandlers';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';
import { fromGlobalId } from '@shared/utils/global-id';

import { REPORT_PREVIEW_ACTIONS_ANCHOR } from './previewAnchors';

const SHARE_TYPES = {
  edit: 'edit',
  share: 'copy_link',
  download: 'download_pdf',
  print: 'print',
} as const;

type ShareAction = keyof typeof SHARE_TYPES;

const buttonSx = {
  px: 2,
  py: 0.75,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.06)',
} as const;

const PreviewActions = () => {
  const { t } = useLocale();
  const { me } = useAppData();
  const { track } = useAnalytics();
  const {
    documentId,
    templateId,
    documentType,
    language,
    activeIframeRef,
    renderFilename,
    data,
    readOnly,
  } = useReportContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { showCopiedTooltip, onCopied } = useCopiedTooltip();

  const reportId = fromGlobalId(documentId);
  const reportSource = 'report_source' in data ? data.report_source : null;
  const isBrochure = documentType === Doc_Template_Type_Enum_Enum.LotBrochure;
  const editPath = isBrochure
    ? `/brochure-reports/${reportId}/page-cover`
    : `/cma-reports/${reportId}/page-cover`;

  const documentUrl = useDocumentUrl(templateId, documentId, language);

  const { copyLink, isMinting, copiedWithoutTracking, resetCopyWarning } =
    useTrackedDocumentLink(documentUrl, documentType);

  const trackAction = useCallback(
    (action: ShareAction) => {
      if (isBrochure) {
        return;
      }
      const baseProperties = getBaseCmaEventProperties(me, reportId);

      if (baseProperties) {
        track(ANALYTICS_EVENTS.CMA_SHARE_ATTEMPTED, {
          ...baseProperties,
          share_type: SHARE_TYPES[action],
          report_source: reportSource,
          action_location: 'preview',
        });
      }
    },
    [isBrochure, me, reportId, reportSource, track],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const withMenuAction = useCallback(
    (action: ShareAction, handler: () => void) => () => {
      trackAction(action);
      handler();
      handleClose();
    },
    [trackAction, handleClose],
  );

  const handleEdit = () => {
    trackAction('edit');
    window.open(editPath, '_blank', 'noopener,noreferrer');
  };

  const handleShareClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePrint = useReportPrintHandler(activeIframeRef);

  const handleDownload = useReportDownloadHandler(documentUrl);

  return (
    <>
      <Box
        data-tour={REPORT_PREVIEW_ACTIONS_ANCHOR}
        sx={{
          position: 'absolute',
          bottom: 30,
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 1.5,
          zIndex: 10,
        }}
      >
        {!readOnly && (
          <Button
            variant="contained"
            startIcon={<Pencil />}
            onClick={handleEdit}
            sx={buttonSx}
          >
            {t('Edit')}
          </Button>
        )}
        <Tooltip
          describeChild
          open={showCopiedTooltip && !copiedWithoutTracking}
          title={t('Link copied')}
          placement="top"
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          <Button
            variant="contained"
            startIcon={isMinting ? <CircularProgress size={16} /> : <Share2 />}
            onClick={handleShareClick}
            sx={buttonSx}
          >
            {t('Share')}
          </Button>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          transformOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        >
          <MenuItem
            onClick={withMenuAction('share', () => copyLink(onCopied))}
            sx={{ gap: 1.5 }}
          >
            <Link size={16} />
            <ListItemText>{t('Copy link')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={withMenuAction('download', () =>
              handleDownload(renderFilename),
            )}
            sx={{ gap: 1.5 }}
          >
            <Download size={16} />
            <ListItemText>{t('Download')}</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={withMenuAction('print', handlePrint)}
            sx={{ gap: 1.5 }}
          >
            <Printer size={16} />
            <ListItemText>{t('Print')}</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
      {copiedWithoutTracking && (
        <RASnackbar
          message={t('Link copied without tracking')}
          onClose={resetCopyWarning}
          severity="warning"
        />
      )}
    </>
  );
};

export default PreviewActions;
